// Opts Zod out of its `new Function` probe, which a host page's CSP reports as
// a violation. Kept in the graph from here; the ordering guarantee comes from
// the `zod` chunk group in vite.config.ts, not from this import's position.
import '~/modules/zod'
import { defineCustomElement } from 'vue'
import type { App } from 'vue'
import { createPinia } from 'pinia'
import { I18nInjectionKey } from 'vue-i18n'
import { i18n } from 'i18n'
import { VolverPlugin } from '@volverjs/ui-vue'
import { normal } from '@volverjs/ui-vue/icons'
import custom from 'icons'
import { trackAppException } from '~/modules/applicationInsights'

// In lib mode the ceStripBaseStyles plugin removes the @use from each .ce.vue,
// so we import the design system CSS once here and pass it via `styles`.
// In dev mode the .ce.vue files already include the @use, so we skip it.
import baseStyles from '~/assets/scss/style.scss?inline'

// Import the Vue components.
import FloatingChatWidget from '~/components/FloatingChatWidget.ce.vue'
import EmbeddedChatWidget from '~/components/EmbeddedChatWidget.ce.vue'

// Turn the Vue components into Custom Element classes.
function configureApp(app: App) {
    const { t } = i18n.global
    app.use(createPinia())
    app.use(i18n)
    // Inside a custom element, vue-i18n's `useI18n()` injects `I18nInjectionKey`
    // instead of the app-scoped `__VUE_I18N_SYMBOL__` that `app.use(i18n)`
    // provides. Provide the instance under that key too so composition-API
    // `useI18n()` calls resolve within the widget's shadow tree.
    app.provide(I18nInjectionKey, i18n)
    app.use(VolverPlugin, {
        iconsCollections: [normal, custom],
        defaults: {
            VvCombobox: {
                noResultsLabel: t('message.noResults'),
                noOptionsLabel: t('message.noOptions'),
                selectedHintLabel: t('label.selectedHint'),
                deselectActionLabel: t('label.deselectAction'),
                selectHintLabel: t('action.selectHint'),
                deselectHintLabel: t('action.deselectHint'),
                searchPlaceholder: t('placeholder.search'),
                closeLabel: t('action.close'),
                loadingLabel: t('message.loading'),
                strategy: 'fixed',
                autoPlacement: {
                    allowedPlacements: ['top', 'bottom'],
                },
                searchable: true,
            },
            // The library's own loading label is "Loading..."
            VvInputText: {
                loadingLabel: t('message.loading'),
            },
            VvTextarea: {
                loadingLabel: t('message.loading'),
            },
        },
    })

    // Report Vue component errors through the telemetry module, which logs
    // them whether or not the installation turned browser telemetry on.
    app.config.errorHandler = (error, _instance, info) => {
        const normalizedError =
            error instanceof Error ? error : new Error(String(error))
        trackAppException(normalizedError, { vueInfo: info })
    }
}

function getCeOptions(component: unknown) {
    const { styles } = component as { styles?: string[] }
    return {
        configureApp,
        ...(import.meta.env.DEV
            ? {}
            : { styles: [baseStyles, ...(styles ?? [])] }),
    }
}

export const FloatingChatWidgetCe = defineCustomElement(
    FloatingChatWidget,
    getCeOptions(FloatingChatWidget),
)

export const EmbeddedChatWidgetCe = defineCustomElement(
    EmbeddedChatWidget,
    getCeOptions(EmbeddedChatWidget),
)

// Remember to register the element classes with the browser.
customElements.define('floating-chat-widget', FloatingChatWidgetCe)
customElements.define('embedded-chat-widget', EmbeddedChatWidgetCe)

// Add the new element types to Vue's GlobalComponents type.
declare module 'vue' {
    interface GlobalComponents {
        'floating-chat-widget': typeof FloatingChatWidget
        'embedded-chat-widget': typeof EmbeddedChatWidget
    }
}

/** A tool the host page declares and runs: MCP's definition plus the function that runs it. */
export interface HostTool {
    name: string
    description?: string
    inputSchema: Record<string, unknown> & { type: 'object' }
    annotations?: {
        title?: string
        readOnlyHint?: boolean
        destructiveHint?: boolean
    }
    execute(
        input: unknown,
        options: { signal: AbortSignal; callId: string; messageId: string },
    ): Promise<{
        content: Array<{ type: 'text'; text: string }>
        isError?: boolean
    }>
    /**
     * The question the user answers before this call runs, or nothing. It
     * adds a confirmation, never removes one the agent's settings ask for.
     */
    confirmation?(input: unknown): string | undefined
}

// Add the element types to the global HTMLElementTagNameMap for vanilla TS consumers.
export interface FloatingChatWidgetElement extends HTMLElement {
    organizationSlug: string
    agentId: string
    /** The backend that hosts the agent: the Eight Wave cloud unless set. */
    baseUrl?: string
    externalToken?: string
    awaitExternalToken?: boolean
    /** Host-session mode: use the host platform's better-auth session from the cookie (no widget sign-in). */
    useHostSession?: boolean
    floatingButtonPosition?: 'left' | 'right'
    /** `copilot` docks the chat on one side with the site framed beside it; wins over the agent's setting (spec `widget-copilot.md`). */
    layout?: 'floating' | 'copilot'
    /** Floating layout only: `glass` makes the card frosted glass over the page; wins over the agent's setting. */
    appearance?: 'solid' | 'glass'
    customCss?: string
    open(): void
    close(): void
    toggle(): void
    sendMessage(text: string): void
    startNewChat(): void
    setContext(context: Record<string, unknown>): void
    /** Registers a tool this page runs itself when the agent calls it, in the shape of WebMCP's `registerTool` (spec `host-tools.md`). */
    registerTool(tool: HostTool): void
    unregisterTool(name: string): void
    /** The pages of the site the agent may open without asking, `<id>` for any one segment; each call replaces the list. */
    registerPages(pages: { path: string; title?: string }[]): void
    /** Authenticates with an external JWT (same flow as the `external-token` attribute). */
    signIn(externalToken: string): Promise<void>
    /** Signs out and withdraws the active external token (no automatic re-login with it). */
    signOut(): Promise<void>
    readonly isOpen: boolean
    /** The agent is answering or running a tool; false while it waits on the user (spec `widget-copilot.md`). */
    readonly isWorking: boolean
    /** The agent is acting on the page: the control bar and its stop show (spec `page-tools.md`). */
    readonly isControllingPage: boolean
}

export interface EmbeddedChatWidgetElement extends HTMLElement {
    organizationSlug: string
    agentId: string
    /** The backend that hosts the agent: the Eight Wave cloud unless set. */
    baseUrl?: string
    externalToken?: string
    awaitExternalToken?: boolean
    /** Host-session mode: use the host platform's better-auth session from the cookie (no widget sign-in). */
    useHostSession?: boolean
    /** Layout mode: `widget` (default), `fullscreen` (full-page app layout) or `panel` (compact chrome for a docked side panel; the host owns background, border and open/close). */
    displayMode?: 'widget' | 'fullscreen' | 'panel'
    /** The color scheme, over the agent's own setting too (`systemTheme` applies only to an agent set to automatic). */
    theme?: 'light' | 'dark'
    /** While the page has a modal dialog open, the chat moves into it and stays usable (spec `page-tools-improvements.md`, M11). */
    followPageDialogs?: boolean
    /** The chat's menu offers "Close", which dispatches `close-requested`: the host closes the panel. */
    closable?: boolean
    forwardedSlots?: string
    customCss?: string
    sendMessage(text: string): void
    startNewChat(): void
    setContext(context: Record<string, unknown>): void
    /** Registers a tool this page runs itself when the agent calls it, in the shape of WebMCP's `registerTool` (spec `host-tools.md`). */
    registerTool(tool: HostTool): void
    unregisterTool(name: string): void
    /** The pages of the site the agent may open without asking, `<id>` for any one segment; each call replaces the list. */
    registerPages(pages: { path: string; title?: string }[]): void
    /** Authenticates with an external JWT (same flow as the `external-token` attribute). */
    signIn(externalToken: string): Promise<void>
    /** Signs out and withdraws the active external token (no automatic re-login with it). */
    signOut(): Promise<void>
    /** The agent is answering or running a tool; false while it waits on the user (spec `widget-copilot.md`). */
    readonly isWorking: boolean
    /** The agent is acting on the page: the control bar and its stop show (spec `page-tools.md`). */
    readonly isControllingPage: boolean
}

declare global {
    interface HTMLElementTagNameMap {
        'floating-chat-widget': FloatingChatWidgetElement
        'embedded-chat-widget': EmbeddedChatWidgetElement
    }
}
